import { partial } from "ramda";
import React, { useEffect, useRef } from "react";

import Input from "components/input";
import { initGoogleAutocomplete } from "helpers/address";
import { withTracking } from "helpers/analytics";
import { handleErrors } from "helpers/credit_cards";
import { getTargetValue } from "helpers/event";

/**
 * This represents a subset of fields on INewCreditCard.
 */
interface CreditCardAddressFields {
  address1: string;
  city?: string;
  state?: string;
  zip?: string;
}

interface ValidationObject {
  card: CreditCardAddressFields;
  error: string;
  field: keyof CreditCardAddressFields;
  type: string;
}

interface Props {
  autofocusRef: React.MutableRefObject<HTMLInputElement>;
  newCreditCard: CreditCardAddressFields;
  onBlurValidateField: (
    field: keyof CreditCardAddressFields,
    card: CreditCardAddressFields
  ) => ValidationObject;
  onNewCreditCardUpdate: (props: CreditCardAddressFields) => void;
}

const CreditCardAddressAutocomplete = ({
  autofocusRef,
  newCreditCard,
  onBlurValidateField,
  onNewCreditCardUpdate,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const input = inputRef.current;

    if (!input) return;

    const onAddressSelected = ({ address }) => {
      // Update credit card address fields, via redux.
      onNewCreditCardUpdate({
        address1: address.line1,
        city: address.city,
        state: address.state,
        zip: address.zip,
      });

      // Update value of autocomplete field to contain only line 1 of address.
      input.value = address.line1;

      // Autofocus line 2 instead of removing focus altogether.
      autofocusRef?.current?.focus();
    };

    initGoogleAutocomplete({ input, onAddressSelected });
    // added since a new eslint rule,
    // feel free to fix this eslint error if you can test it properly
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBlur = withTracking(
    partial(onBlurValidateField, ["address1", newCreditCard]),
    "OLO - Checkout - enter billing address"
  );

  const handleChange = event => {
    onNewCreditCardUpdate({ address1: getTargetValue(event) });
  };

  const handleError = handleErrors("address1", newCreditCard);

  const inputId = "credit-card-address-1";

  return (
    <>
      <label htmlFor={inputId}>Address 1</label>
      <Input
        error={handleError}
        id={inputId}
        onBlur={handleBlur}
        onChange={handleChange}
        placeholder="Street and number"
        ref={inputRef}
        required
        type="text"
        value={newCreditCard.address1}
      />
    </>
  );
};

export default CreditCardAddressAutocomplete;
